import React, { useCallback, useMemo } from 'react';
import {
  BillingInfo,
  EditPaymentMethod,
  TransactionCategory,
  UsageSelector,
  useBillingContext,
  useEditPaymentMethodContext,
} from '@shipstation/financial-services-ui-components';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { FlexColumn, Gap } from 'ui-components/components/flex';

import { assignedPreferencesMessages } from './messages';
import styles from './styles.module.less';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { getBillingInfoFromShipFrom } from '../../../../../../financial-services-ui/mapping';
import { TaxIdentifierSection } from '../../../../../../pages/settings/payment-and-subscription/components/tax-identifier-section';
import Global from '../../../../../../store/global-selectors';
import { AddressDropdown } from '../../../address-dropdown';

/**
 * @param {object} props
 * @param {(data?: TransactionCategory[]) => void} props.onUsageSelectorChange
 * @param {import('@shipstation/financial-services-ui-components').BillingInfoProps['setPerformFormValidation']} props.setPerformFormValidation
 * @returns {JSX.Element}
 */
export const EditPaymentMethodBody = ({ onUsageSelectorChange, setPerformFormValidation }) => {
  const intl = useIntl();
  const { billingInfo, isEditing, setBillingInfo } = useBillingContext();
  const { paymentMethod } = useEditPaymentMethodContext();
  const shipFroms = useSelector(Global.selectSellerShipFroms) || emptyArray;

  const isSubscriptionActive = paymentMethod.assignedPreferences?.includes(TransactionCategory.Subscription);
  const isAddFundsActive = paymentMethod.assignedPreferences?.includes(TransactionCategory.AddFunds);

  const usageOptions = useMemo(
    () => [
      {
        id: TransactionCategory.Subscription,
        value: isSubscriptionActive,
        label: intl.formatMessage(assignedPreferencesMessages[TransactionCategory.Subscription]),
        disabled: isSubscriptionActive,
      },
      {
        id: TransactionCategory.AddFunds,
        value: isAddFundsActive,
        label: intl.formatMessage(assignedPreferencesMessages[TransactionCategory.AddFunds]),
        disabled: isAddFundsActive,
      },
    ],
    [intl, isAddFundsActive, isSubscriptionActive],
  );

  const handleAddressChange = useCallback(
    (addressSelection) => {
      setBillingInfo(getBillingInfoFromShipFrom(addressSelection?.originAddress) || emptyObject, true);
    },
    [setBillingInfo],
  );

  return (
    <FlexColumn gap={Gap.Space24}>
      <EditPaymentMethod />

      {billingInfo ? (
        <div className={styles.wrapper}>
          {isEditing && !!shipFroms?.length ? <AddressDropdown onAddressChange={handleAddressChange} shipFroms={shipFroms} /> : null}
          <BillingInfo setPerformFormValidation={setPerformFormValidation} />
        </div>
      ) : null}

      <UsageSelector
        checkboxClassName={styles['usage-selector-wrapper']}
        onUsageChange={onUsageSelectorChange}
        usageOptions={usageOptions}
      />

      <TaxIdentifierSection formClassName={styles['tax-identifier-form']} />
    </FlexColumn>
  );
};
